/* eslint-disable jsx-a11y/no-static-element-interactions */
import { createRef, PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import debounce from '@spotahome/marketplace-common/src/utils/debounce';

import { Provider } from './utils/viewer360Context';
import SliderGallery from './SliderGallery';

import './FullScreenSlider.scss';

const getPanoramicHeight = width => Math.floor((width * 9) / 16);
const getPanoramicWidth = height => Math.floor((height * 16) / 9);

const isLandscapeLayout = () =>
  // eslint-disable-next-line no-restricted-globals
  screen.width / screen.height > 1.77 && screen.width < 1024;

const INITIAL_DIMENSIONS = {
  height: 'auto',
  width: 'auto'
};

class FullScreenSlider extends PureComponent {
  state = {
    ...INITIAL_DIMENSIONS,
    isLandscape: false,
    shouldUpdate: false
  };

  containerRef = createRef();
  counterRef = createRef();
  carouselRef = createRef();

  rearrangeLayout = debounce(() => {
    if (this.containerRef.current) {
      this.setState(this.updateArrangement());
    }
  });

  componentDidMount() {
    window.addEventListener('resize', this.rearrangeLayout);
    this.setState(this.rearrangeLayout());
  }

  componentDidUpdate = () => {
    if (this.state.shouldUpdate) {
      this.updateDimensions();
    }
  };

  componentWillUnmount() {
    window.removeEventListener('resize', this.rearrangeLayout);
  }

  handleFullScreenSliderClick = event => {
    if (event.target === this.containerRef.current) {
      this.props.handleClose();
    }
  };

  handleRedimensionOnTrackChange = () => {
    this.updateDimensions();
  };

  getContainerHeight = () => {
    const container = this.containerRef.current;
    const height = container.clientHeight;
    const { paddingTop, paddingBottom } = getComputedStyle(container);

    return height - parseInt(paddingTop) - parseInt(paddingBottom);
  };

  getCounterHeight = () =>
    this.counterRef && this.counterRef.current
      ? this.counterRef.current.clientHeight
      : 0;

  getCarouselWidth = () => {
    const carousel = this.carouselRef.current;
    const height = carousel.clientWidth;
    const { marginLeft, marginRight } = getComputedStyle(carousel);
    return height + parseInt(marginLeft) + parseInt(marginRight);
  };

  getCarouselHeight = () => {
    const carousel = this.carouselRef.current;
    const height = carousel.scrollHeight;
    const { paddingTop, paddingBottom } = getComputedStyle(carousel);
    return height + parseInt(paddingTop) + parseInt(paddingBottom);
  };

  updateDimensions = () => {
    const newDimensions = {};
    const counterHeight = this.getCounterHeight();
    const containerHeight = this.getContainerHeight();

    let availableHeight = containerHeight;

    if (this.state.isLandscape) {
      const containerWidth = this.containerRef.current.clientWidth;
      const availableWidth = containerWidth - this.getCarouselWidth();
      newDimensions.width = availableWidth;
      newDimensions.height = getPanoramicHeight(availableWidth) + counterHeight;
    } else {
      availableHeight -= this.getCarouselHeight();
      newDimensions.width = '100%';
      newDimensions.height =
        getPanoramicHeight(this.containerRef.current.clientWidth) +
        counterHeight;
    }

    if (newDimensions.height > availableHeight) {
      newDimensions.height = availableHeight;
      newDimensions.width = getPanoramicWidth(availableHeight - counterHeight);
    }

    this.setState({
      ...INITIAL_DIMENSIONS,
      ...newDimensions,
      shouldUpdate: false
    });
  };

  updateArrangement = () => {
    const isLandscape = isLandscapeLayout();

    return { isLandscape, shouldUpdate: true };
  };

  render() {
    const { isLandscape } = this.state;
    const { currentTrackItem } = this.props;

    const className = classNames('fullscreen-slider', {
      'fullscreen-slider--inline': isLandscape
    });
    return (
      <div
        ref={this.containerRef}
        className={className}
        onClick={this.handleFullScreenSliderClick}
      >
        <Provider value={{ isLandscape }}>
          <SliderGallery
            isLandscape={isLandscape}
            tracks={this.props.tracks}
            height={this.state.height}
            width={this.state.width}
            counterRef={this.counterRef}
            carouselRef={this.carouselRef}
            defaultTrackToShow={this.props.defaultTrackToShow}
            onTrackChange={this.handleRedimensionOnTrackChange}
            currentTrackItem={currentTrackItem}
            listingId={this.props.listingId}
          />
        </Provider>
      </div>
    );
  }
}

FullScreenSlider.propTypes = {
  tracks: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  defaultTrackToShow: PropTypes.number.isRequired,
  handleClose: PropTypes.func,
  listingId: PropTypes.string.isRequired,
  currentTrackItem: PropTypes.number.isRequired
};

FullScreenSlider.defaultProps = {
  handleClose: PropTypes.func
};

export default FullScreenSlider;
